import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../services/auth';
import { migrationApi } from '../services/api';
import Pagination from '../components/Pagination';
import { STATUS } from '../constants/constants';

const MigrationItemUpdated = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [migrationRequest, setMigrationRequest] = useState(null);
  const [migrationRequestItems, setMigrationRequestItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startingMigration, setStartingMigration] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [statusCounts, setStatusCounts] = useState({
    ALL: 0,
    PENDING: 0,
    DONE: 0,
    ERROR: 0
  });

  // Check if user is authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  // Fetch migration request and its items on component mount
  useEffect(() => {
    fetchMigrationRequest();
    fetchMigrationRequestItems();
    fetchStatusCounts();
  }, [id]);

  // Refetch data when filter or pagination changes
  useEffect(() => {
    fetchMigrationRequestItems();
  }, [currentPage, itemsPerPage, statusFilter]);

  const fetchMigrationRequest = async () => {
    try {
      const response = await migrationApi.getNewMigrationRequest(id);

      if (response.data) {
        if (response.data.data) {
          setMigrationRequest(response.data.data);
        }
        else if (response.data.response) {
          setMigrationRequest(response.data.response);
        }
        else {
          setMigrationRequest(response.data);
        }
      }
    } catch (err) {
      console.error('Error fetching migration request:', err);
      setError('Failed to load migration request. Please try again.');
    }
  };

  const fetchMigrationRequestItems = async () => {
    setLoading(true);
    setError(null);
    try {
      let apiStatusFilter = null;
      if (statusFilter !== 'ALL') {
        const statusMap = {
          'PENDING': STATUS.PENDING,
          'DONE': STATUS.DONE,
          'ERROR': STATUS.ERROR
        };
        apiStatusFilter = statusMap[statusFilter];
      }

      const response = await migrationApi.getMigrationItemsByRequestId(
        id,
        currentPage,
        itemsPerPage,
        apiStatusFilter
      );

      if (response.data) {
        let items = [];
        let paginationInfo = null;

        if (response.data.data && Array.isArray(response.data.data)) {
          items = response.data.data;
          paginationInfo = response.data.pagination;
        } else if (response.data.response && response.data.response.data) {
          items = response.data.response.data;
          paginationInfo = response.data.response.pagination;
        } else if (Array.isArray(response.data)) {
          items = response.data;
        }

        if (paginationInfo) {
          setTotalItems(paginationInfo.totalItem);
          setTotalPages(paginationInfo.totalPage);
        }

        setMigrationRequestItems(items || []);
      }
    } catch (err) {
      console.error('Error fetching migration request items:', err);
      setError('Failed to load migration request items. Please try again.');
      setMigrationRequestItems([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusCounts = async () => {
    try {
      const response = await migrationApi.getStatusCount(id);

      if (response.data && response.data.data) {
        const counts = response.data.data;
        setStatusCounts({
          ALL: (counts.Queuing || 0) + (counts.Done || 0) + (counts.Error || 0),
          PENDING: counts.Queuing || 0,
          DONE: counts.Done || 0,
          ERROR: counts.Error || 0,
        });
      }
    } catch (err) {
      console.error('Error fetching status counts:', err);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      [STATUS.PENDING]: { label: 'Pending', className: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-400' },
      [STATUS.DONE]: { label: 'Done', className: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-400' },
      [STATUS.ERROR]: { label: 'Error', className: 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-400' },
    };

    const config = statusConfig[status] || { label: 'Unknown', className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const handleStartMigration = async () => {
    setStartingMigration(true);
    try {
      await migrationApi.startMigration(id);
      alert('Migration started successfully!');
      fetchMigrationRequestItems(); // Refresh the items
    } catch (err) {
      console.error('Error starting migration:', err);
      alert('Failed to start migration. Please try again.');
    } finally {
      setStartingMigration(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-lg">
        <strong className="font-bold">Error:</strong> {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <button
            onClick={() => navigate('/migration')}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Migrations
          </button>
          <h1 className="text-title-sm font-semibold text-gray-900 dark:text-white mt-4">
            Migration Details #{id}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            View and manage items for this migration request.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleStartMigration}
            disabled={startingMigration}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-success-600 border border-transparent rounded-lg hover:bg-success-700 focus:outline-none focus:ring-4 focus:ring-success-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className={`w-4 h-4 ${startingMigration ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 00.707-.293l1.414-1.414A1 1 0 0012 8h2a1 1 0 011 1v3" />
            </svg>
            {startingMigration ? 'Starting...' : 'Start Migration'}
          </button>
          <button 
            onClick={() => {
              fetchMigrationRequestItems();
              fetchStatusCounts();
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Migration Info Card */}
      {migrationRequest && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Migration Request Information
            </h3>
          </div>
          <div className="px-6 py-4">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Source Game</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  <div className="font-medium">{migrationRequest.categorySource}</div>
                  <div className="text-xs text-gray-400 truncate">{migrationRequest.linkSource}</div>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Destination Game</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  <div className="font-medium">{migrationRequest.categoryDes}</div>
                  <div className="text-xs text-gray-400 truncate">{migrationRequest.linkDes}</div>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                <dd className="mt-1">
                  {getStatusBadge(migrationRequest.status)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created At</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {migrationRequest.createdAt ? new Date(migrationRequest.createdAt).toLocaleString() : '—'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {/* Status Filters */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Filter by Status</h3>
        </div>
        <div className="px-6 py-4">
          <div className="flex flex-wrap gap-3">
            {Object.entries(statusCounts).map(([status, count]) => (
              <button
                key={status}
                onClick={() => {
                  setStatusFilter(status);
                  setCurrentPage(1);
                }}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === status
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <span>{status}</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-700">
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Items Table */}
      {migrationRequestItems.length > 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Migration Items ({migrationRequestItems.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    VIP Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Message
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                {migrationRequestItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {item.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {item.title || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {item.level !== null && item.level !== undefined ? item.level : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {item.vipRank !== null && item.vipRank !== undefined ? item.vipRank : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(item.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                      <span className="truncate block">
                        {item.message || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : !loading && (
        <div className="text-center py-12 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No items found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            No migration items found for the current filter.
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}
    </div>
  );
};

export default MigrationItemUpdated;
